// BqDatasetPanelV2 — operator's per-source BigQuery dataset surface.
//
// Vision (2026-05-09): Phase 1 = data lands in GCS, every promote_to_table
// trigger creates a BQ external table + view over those GCS objects so
// the dataset is queryable instantly. Operator composes additional
// views on top. Phase 2 (future, P5 auto-tier) will materialise hot
// views as native BQ tables — same view names, same query API; the
// operator just sees "lower latency, higher cost" tier transition
// metadata. Phase 1 IS the product today.
//
// Surface: per-source dataset header (`datahub_<hash>`) + every view in
// it (whether from a promote_to_table trigger, :auto-connect-bq fan-out,
// or this panel's "+ New view" button). Per view: copyable SQL, deep
// links to BQ console / Sheets / Looker, edit/drop. The list reads from
// /api/v1/sources/:id/bq-views which BQ-side filters to type=VIEW so
// every materialisation path lands in one list.

import React, { useEffect, useState } from 'react';
import {
  listBqViews,
  createBqView,
  updateBqView,
  deleteBqView,
  type BqView,
} from '../../api';
import { useToast } from '../Toast';
import { RecipeBuilderModal } from './RecipeBuilderModal';
import { InlineRecipeEditorV2 } from './InlineRecipeEditorV2';

const PROJECT_ID = 'hub-mega-prod';

export function BqDatasetPanelV2({ sourceId }: { sourceId: string }): React.ReactElement {
  const toast = useToast();
  const [data, setData] = useState<{ dataset: string; views: BqView[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<BqView | null>(null);
  const [busyView, setBusyView] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function reload(): void {
    setLoading(true);
    listBqViews(sourceId)
      .then(r => { setData({ dataset: r.dataset, views: r.views }); setError(null); })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }
  useEffect(() => { reload(); }, [sourceId]);

  async function handleDelete(viewName: string): Promise<void> {
    const ok = await toast.confirm({
      title: 'Drop this view?',
      message: `BigQuery view ${data?.dataset}.${viewName} will be deleted. The underlying GCS data + external table are not affected; you can recreate the view later.\n\nType the view name to confirm.`,
      destructive: true,
      confirmLabel: 'Drop view',
      requireType: viewName,
    });
    if (!ok) return;
    setBusyView(viewName);
    try {
      await deleteBqView(sourceId, viewName);
      toast.show(`View ${viewName} dropped.`, { kind: 'success' });
      reload();
    } catch (e) {
      toast.show(`Drop failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setBusyView(null);
    }
  }

  function toggleExpanded(name: string): void {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  if (loading && !data) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>Loading dataset…</div>;
  }
  if (error) {
    return <div style={{ padding: 12, color: 'var(--danger)', fontSize: 12.5 }}>{error}</div>;
  }
  if (!data) return <></>;

  const fqDataset = `${PROJECT_ID}.${data.dataset}`;

  return (
    <div>
      {/* Dataset header — the per-source BQ workspace */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>BigQuery dataset</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <code style={{ fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--ink)', fontWeight: 500 }}>{fqDataset}</code>
            <button
              type="button"
              className="btn sm ghost"
              onClick={() => { void navigator.clipboard.writeText(fqDataset); toast.show('Dataset name copied', { kind: 'success' }); }}
              title="Copy fully-qualified dataset name"
              style={{ padding: '2px 8px', fontSize: 11 }}
            >copy</button>
            <a
              href={`https://console.cloud.google.com/bigquery?project=${PROJECT_ID}&p=${PROJECT_ID}&d=${data.dataset}&page=dataset`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn sm ghost"
              style={{ padding: '2px 8px', fontSize: 11, textDecoration: 'none' }}
              title="Open dataset in BigQuery console"
            >open in BQ ↗</a>
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.5, maxWidth: 720 }}>
            Every event for this source lands in GCS; <code style={{ fontFamily: 'var(--mono)' }}>promote_to_table</code> triggers create
            views over that data. Zero fixed storage cost — pay only on query scan. New events are auto-included on the next query.
          </div>
        </div>
        <button className="btn sm primary" onClick={() => setShowCreate(true)}>+ New view</button>
      </div>

      {data.views.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 8, color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.6 }}>
          <div style={{ fontSize: 14, color: 'var(--ink-2)', marginBottom: 8 }}>Workspace ready, no views materialised yet.</div>
          The default <code style={{ fontFamily: 'var(--mono)' }}>events</code> view will appear after the first event arrives.<br />
          Click <strong>+ New view</strong> to write a custom SQL view ahead of time.
        </div>
      )}

      {/* Inline recipe editor — primary working surface. Operator picks
          a base view, adds filter rows, and saves the result as a new
          view. Same recipe drives an optional stream-time trigger. */}
      {data.views.length > 0 && (
        <InlineRecipeEditorV2
          sourceId={sourceId}
          dataset={data.dataset}
          views={data.views}
          onSaved={reload}
        />
      )}

      {/* Saved views list — secondary surface, sits below the editor for
          managing existing views (BQ link, Sheets link, edit, drop). */}
      {data.views.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', userSelect: 'none' }}>
            Saved views ({data.views.length}) — manage / open in BQ / drop
          </summary>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.views.map(v => {
            const isExpanded = expanded.has(v.name);
            const fqView = `${fqDataset}.${v.name}`;
            return (
              <div key={v.name} style={{ background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => toggleExpanded(v.name)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--ink-3)', fontFamily: 'var(--mono)', fontSize: 12 }}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >{isExpanded ? '▾' : '▸'}</button>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--ink)', fontWeight: 500 }}>{v.name}</span>
                  {v.modified_at && <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)' }}>updated {new Date(v.modified_at).toLocaleDateString()}</span>}
                  <span style={{ flex: 1, minWidth: 0 }} />
                  <a
                    href={`https://console.cloud.google.com/bigquery?project=${PROJECT_ID}&p=${PROJECT_ID}&d=${data.dataset}&t=${v.name}&page=table`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn sm ghost"
                    style={{ padding: '2px 8px', fontSize: 11, textDecoration: 'none' }}
                    title="Open this view in BigQuery console"
                  >BQ ↗</a>
                  <a
                    href={`https://docs.google.com/spreadsheets/create?usp=sheets_bq_data&p=${PROJECT_ID}&d=${data.dataset}&t=${v.name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn sm ghost"
                    style={{ padding: '2px 8px', fontSize: 11, textDecoration: 'none' }}
                    title={`Open Google Sheets and connect to ${PROJECT_ID}.${data.dataset}.${v.name} via Connected Sheets.\n\nIf the deep link doesn't auto-import, in Sheets: Data → Data connectors → BigQuery → ${data.dataset} → ${v.name}.\n\nOnce connected, every refresh shows the latest events.`}
                  >Sheets ↗</a>
                  <button
                    className="btn sm ghost"
                    style={{ padding: '2px 8px', fontSize: 11 }}
                    disabled={busyView === v.name}
                    onClick={() => setEditing(v)}
                  >edit</button>
                  <button
                    className="btn sm ghost"
                    style={{ padding: '2px 8px', fontSize: 11, color: 'var(--danger)' }}
                    disabled={busyView === v.name}
                    onClick={() => { void handleDelete(v.name); }}
                  >drop</button>
                </div>
                {v.description && (
                  <div style={{ padding: '0 12px 8px 32px', fontSize: 12, color: 'var(--ink-2)' }}>{v.description}</div>
                )}
                {isExpanded && (
                  <div style={{ padding: '0 12px 12px 32px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)' }}>{fqView}</span>
                      <button
                        type="button"
                        className="btn sm ghost"
                        onClick={() => { void navigator.clipboard.writeText(`SELECT * FROM \`${fqView}\` LIMIT 100`); toast.show('Sample SQL copied', { kind: 'success' }); }}
                        title="Copy a sample SELECT for this view"
                        style={{ padding: '1px 7px', fontSize: 10 }}
                      >copy SELECT</button>
                    </div>
                    <pre style={{
                      margin: 0, padding: '8px 10px',
                      background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 4,
                      fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-2)',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 240, overflow: 'auto',
                    }}>{v.sql}</pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </details>
      )}

      {/* Create flow: visual recipe builder. Edit flow: raw SQL editor
          (for now — recipe-builder edit-mode round-trip lands later). */}
      {showCreate && !editing && (
        <RecipeBuilderModal
          sourceId={sourceId}
          dataset={data.dataset}
          baseView={data.views.find(v => v.name === 'events') ? 'events' : (data.views[0]?.name ?? 'events')}
          onCancel={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            reload();
            toast.show('View created.', { kind: 'success' });
          }}
        />
      )}
      {editing && (
        <BqViewDialog
          sourceId={sourceId}
          dataset={data.dataset}
          editing={editing}
          existingNames={data.views.map(v => v.name)}
          onCancel={() => setEditing(null)}
          onSaved={(mode) => {
            setEditing(null);
            reload();
            toast.show(mode === 'edit' ? 'View updated.' : 'View created.', { kind: 'success' });
          }}
        />
      )}
    </div>
  );
}

/** Modal SQL editor for create + edit. */
function BqViewDialog({ sourceId, dataset, editing, existingNames, onCancel, onSaved }: {
  sourceId: string;
  dataset: string;
  editing: BqView | null;
  existingNames: string[];
  onCancel: () => void;
  onSaved: (mode: 'create' | 'edit') => void;
}): React.ReactElement {
  const toast = useToast();
  const isEdit = editing !== null;
  const [name, setName] = useState(editing?.name ?? '');
  // Default SQL composes on top of the base `events` view. Vision:
  // filtered/processed views are composed on top of the dataset's base
  // view — operators rarely need to drop down to JSON_VALUE on the
  // external table directly. The same SQL idioms below also describe
  // the trigger logic surface: filter == validate_required at stream
  // time, unique == validate_dedupe, project == field_map. Write the
  // recipe once as a view; future iterations let you flip "use as
  // stream gate" so the same predicate runs at ingest.
  const baseView = existingNames.includes('events') ? 'events' : (existingNames[0] ?? 'events');
  const fqBase = `\`${dataset}.${baseView}\``;
  const TEMPLATES = {
    blank: {
      label: 'Blank',
      blurb: 'Start from scratch.',
      sql: `SELECT *\nFROM ${fqBase}\nLIMIT 100`,
    },
    filter: {
      label: 'Filter (where clause)',
      blurb: 'Equivalent to validate_required at stream time.',
      sql: `SELECT *\nFROM ${fqBase}\nWHERE JSON_VALUE(payload, '$.field_name') IS NOT NULL\n  AND received_at >= TIMESTAMP_SUB(CURRENT_TIMESTAMP(), INTERVAL 30 DAY)`,
    },
    unique: {
      label: 'Unique on key',
      blurb: 'Last-write-wins per business key. Equivalent to validate_dedupe at stream time.',
      sql: `-- Keep only the latest event per business key (last-write-wins).\n-- Same logic as validate_dedupe at stream time.\nSELECT * EXCEPT(rn) FROM (\n  SELECT *,\n    ROW_NUMBER() OVER (\n      PARTITION BY JSON_VALUE(payload, '$.order_id')\n      ORDER BY received_at DESC\n    ) AS rn\n  FROM ${fqBase}\n)\nWHERE rn = 1`,
    },
    project: {
      label: 'Pick fields',
      blurb: 'Rename + project a stable shape. Equivalent to field_map at stream time.',
      sql: `-- Project a stable, named shape over the JSON payload.\nSELECT\n  event_id,\n  source_id,\n  received_at,\n  JSON_VALUE(payload, '$.order_id')   AS order_id,\n  CAST(JSON_VALUE(payload, '$.amount') AS FLOAT64) AS amount,\n  JSON_VALUE(payload, '$.currency')    AS currency\nFROM ${fqBase}`,
    },
    aggregate: {
      label: 'Aggregate (rollup)',
      blurb: 'Group + count/sum. Query-time only — no stream equivalent.',
      sql: `-- Daily rollup of events by type.\nSELECT\n  DATE(received_at) AS day,\n  JSON_VALUE(payload, '$.event_type') AS event_type,\n  COUNT(*) AS event_count\nFROM ${fqBase}\nGROUP BY day, event_type\nORDER BY day DESC, event_count DESC`,
    },
  } as const;
  type TemplateId = keyof typeof TEMPLATES;
  const [template, setTemplate] = useState<TemplateId>(isEdit ? 'blank' : 'filter');
  const [sql, setSql] = useState(editing?.sql ?? TEMPLATES.filter.sql);
  const [description, setDescription] = useState(editing?.description ?? '');
  const [submitting, setSubmitting] = useState(false);

  function pickTemplate(t: TemplateId): void {
    setTemplate(t);
    setSql(TEMPLATES[t].sql);
  }

  async function submit(): Promise<void> {
    if (!sql.trim()) return;
    if (!isEdit && !name.trim()) {
      toast.show('Pick a name for the new view.', { kind: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await updateBqView(sourceId, editing.name, { sql: sql.trim(), description: description.trim() });
        onSaved('edit');
      } else {
        await createBqView(sourceId, { name: name.trim(), sql: sql.trim(), description: description.trim() || undefined });
        onSaved('create');
      }
    } catch (e) {
      toast.show(`Save failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 60, background: 'oklch(0.18 0.01 250 / 0.45)',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 60,
    }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 10,
        width: 'min(720px, 92vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{isEdit ? `Edit view: ${editing.name}` : 'New BigQuery view'}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>{dataset}{isEdit ? `.${editing.name}` : ''}</div>
        </div>
        <div style={{ flex: 1, padding: '14px 18px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {!isEdit && (
            <>
              <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="recent_orders"
                style={{
                  padding: '8px 10px', fontFamily: 'var(--mono)', fontSize: 13,
                  border: '1px solid var(--line)', borderRadius: 4, background: 'var(--bg)', color: 'var(--ink)',
                }}
              />
              <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>letters, digits, underscore · 100 chars max</div>
            </>
          )}
          {!isEdit && (
            <>
              <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginTop: 6 }}>Recipe</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(Object.keys(TEMPLATES) as TemplateId[]).map(t => {
                  const active = template === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => pickTemplate(t)}
                      style={{
                        padding: '4px 10px', fontSize: 11.5, fontFamily: 'var(--ui)',
                        background: active ? 'var(--accent-soft)' : 'var(--bg)',
                        border: `1px solid ${active ? 'var(--accent-line)' : 'var(--line)'}`,
                        color: active ? 'var(--accent)' : 'var(--ink-2)',
                        borderRadius: 14, cursor: 'pointer',
                        fontWeight: active ? 500 : 400,
                      }}
                      title={TEMPLATES[t].blurb}
                    >{TEMPLATES[t].label}</button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic', lineHeight: 1.5 }}>
                {TEMPLATES[template].blurb}
              </div>
            </>
          )}
          <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginTop: 6 }}>SQL</label>
          <textarea
            value={sql}
            onChange={e => setSql(e.target.value)}
            spellCheck={false}
            style={{
              minHeight: 240, padding: '10px 12px',
              fontFamily: 'var(--mono)', fontSize: 12.5, lineHeight: 1.5,
              border: '1px solid var(--line)', borderRadius: 4,
              background: 'var(--bg)', color: 'var(--ink)',
              resize: 'vertical',
            }}
          />
          <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginTop: 6 }}>Description <span style={{ textTransform: 'none', color: 'var(--ink-3)' }}>· optional</span></label>
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What this view exposes — for downstream consumers"
            style={{
              padding: '8px 10px', fontSize: 12.5,
              border: '1px solid var(--line)', borderRadius: 4, background: 'var(--bg)', color: 'var(--ink)',
            }}
          />
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn sm ghost" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button className="btn sm primary" onClick={() => { void submit(); }} disabled={submitting || !sql.trim() || (!isEdit && !name.trim())}>
            {submitting ? 'Saving…' : (isEdit ? 'Save view' : 'Create view')}
          </button>
        </div>
      </div>
    </div>
  );
}
